import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bell, School, BookOpen, Users, User,
  Paperclip, FileText, X, Send, RefreshCw, CheckCircle, Loader2,
} from 'lucide-react';
import Cookies from 'js-cookie';
import { toastMessage } from '@/utils/toast-message';
import { apiRequest } from '@/utils/api-request';
import MultiClassSelect, { CurriculumGroup, ClassFilterChips } from '@/components/common/MultiClassSelect';
import {
  notificationsApi,
  AudienceType, NotifType,
  PaClassItem, StudentItem, BroadcastRecord,
} from './notifications.api';

// ─── Notification type config ─────────────────────────────────────────────────

const NOTIF_TYPES: { value: NotifType; label: string; cat: string; group: string }[] = [
  // → Announcements screen
  { value: 'announcement',  label: 'Announcement',    cat: 'cat-blue',   group: 'Announcements screen' },
  { value: 'event',         label: 'School Event',    cat: 'cat-purple', group: 'Announcements screen' },
  { value: 'holiday',       label: 'Holiday Notice',  cat: 'cat-teal',   group: 'Announcements screen' },
  // → PTM & Meetings screen
  { value: 'ptm',           label: 'PTM / Meeting',   cat: 'cat-purple', group: 'PTM & Meetings screen' },
  // → Academics (Attendance)
  { value: 'attendance',    label: 'Attendance Alert',cat: 'cat-amber',  group: 'Attendance screen'    },
  { value: 'exam',          label: 'Exam / Test',     cat: 'cat-rose',   group: 'Attendance screen'    },
  // → Fees screen
  { value: 'fee_updated',   label: 'Fee Reminder',    cat: 'cat-indigo', group: 'Fees screen'          },
  // → Gallery screen
  { value: 'gallery',       label: 'New Photos',      cat: 'cat-peach',  group: 'Gallery screen'       },
];

const AUDIENCE_OPTIONS: { value: AudienceType; label: string; description: string; icon: React.ReactNode }[] = [
  { value: 'school',  label: 'Entire School',     description: 'All parents in this school',             icon: <School   size={16} strokeWidth={2} /> },
  { value: 'class',   label: 'By Class',           description: 'All sections of a class (e.g. LKG)',    icon: <BookOpen size={16} strokeWidth={2} /> },
  { value: 'section', label: 'By Section',         description: 'A specific class-section (e.g. LKG A)', icon: <Users    size={16} strokeWidth={2} /> },
  { value: 'student', label: 'Specific Student',   description: 'Parents of one student',                 icon: <User     size={16} strokeWidth={2} /> },
];

const TYPE_LABELS: Record<string, string> = Object.fromEntries(NOTIF_TYPES.map(t => [t.value, t.label]));
const TYPE_CAT:    Record<string, string> = Object.fromEntries(NOTIF_TYPES.map(t => [t.value, t.cat]));

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function audienceLabel(r: BroadcastRecord) {
  switch (r.audience_type) {
    case 'school':  return 'Entire School';
    case 'class':   return r.audience_id ? `Class (id ${r.audience_id})` : 'Multiple Classes';
    case 'section': return `Section (id ${r.audience_id})`;
    case 'student': return `Student (id ${r.audience_id})`;
  }
}

// ─── Compose Tab ─────────────────────────────────────────────────────────────

function ComposeTab() {
  const [audienceType,     setAudienceType]     = useState<AudienceType>('school');
  const [notifType,        setNotifType]         = useState<NotifType>('announcement');
  const [title,            setTitle]             = useState('');
  const [body,             setBody]              = useState('');
  const [meetingDate,      setMeetingDate]       = useState('');
  const [venue,            setVenue]             = useState('');
  const [sending,          setSending]           = useState(false);
  const [sendPush,         setSendPush]          = useState(true);
  const [attachmentFiles,  setAttachmentFiles]   = useState<File[]>([]);
  const [previews,         setPreviews]          = useState<(string | null)[]>([]);
  const [classes,           setClasses]           = useState<PaClassItem[]>([]);
  const [classGroups,       setClassGroups]       = useState<CurriculumGroup[]>([]);
  // Multi-class for "By Class" audience type
  const [selectedClassIds,  setSelectedClassIds]  = useState<string[]>([]);
  // Single class for "By Section" audience type
  const [selectedClass,     setSelectedClass]     = useState<PaClassItem | null>(null);
  const [sections,          setSections]          = useState<{ id: number; section_label: string }[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  const [students,         setStudents]          = useState<StudentItem[]>([]);
  const [selectedStudent,  setSelectedStudent]   = useState<StudentItem | null>(null);
  const [studentSearch,    setStudentSearch]     = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const MAX_FILES = 15;


  useEffect(() => {
    notificationsApi.getClasses().then(res => {
      if (res.success && res.data) {
        const grouped: any[] = (res.data as any).data?.grouped ?? (res.data as any).grouped ?? [];
        // Store as CurriculumGroup[] for MultiClassSelect
        setClassGroups(grouped.map((g: any) => ({
          curriculum_id: g.curriculum_id,
          curriculum_name: g.curriculum_name,
          classes: (g.classes ?? []).map((c: any) => ({ id: c.id, class_label: c.class_label })),
        })));
        // Keep flat list for "By Section" single-class picker
        const flat: PaClassItem[] = grouped.flatMap((g: any) =>
          (g.classes ?? []).map((c: any) => ({
            id: c.id,
            class_name: c.class_label,
            curriculum_name: g.curriculum_name,
            curriculum_class_id: c.id,
            section_id: null,
            is_active: true,
          }))
        );
        setClasses(flat);
      }
    });
  }, []);

  useEffect(() => { setSelectedClassIds([]); setSelectedClass(null); setSelectedStudent(null); setSections([]); setSelectedSectionId(null); }, [audienceType]);

  useEffect(() => {
    if (audienceType !== 'section' || !selectedClass) {
      setSections([]);
      setSelectedSectionId(null);
      return;
    }
    const curriculumClassId = selectedClass.curriculum_class_id ?? selectedClass.id;
    apiRequest<any>(`/api/parent-app/admin/classes/${curriculumClassId}/sections`).then(res => {
      const list = res?.data?.data ?? [];
      setSections(Array.isArray(list) ? list : []);
    });
  }, [audienceType, selectedClass]);

  const searchStudents = useCallback((q: string) => {
    setStudentSearch(q);
    if (q.length < 2) { setStudents([]); return; }
    notificationsApi.getStudents(q).then(res => {
      if (res.success && res.data) {
        const rows = (res.data as any).data ?? res.data;
        setStudents(rows);
      }
    });
  }, []);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) { toastMessage('Title and message are required', 'error'); return; }
    if (audienceType === 'class' && !selectedClassIds.length) {
      toastMessage('Please select at least one class', 'error'); return;
    }
    if (audienceType === 'section' && !selectedClass) {
      toastMessage('Please select a class', 'error'); return;
    }
    if (audienceType === 'section' && sections.length > 0 && !selectedSectionId) {
      toastMessage('Please select a section', 'error'); return;
    }
    if (audienceType === 'student' && !selectedStudent) {
      toastMessage('Please select a student', 'error'); return;
    }

    const fd = new FormData();
    fd.append('audience_type', audienceType);
    fd.append('type', notifType);
    fd.append('title', title.trim());
    fd.append('body', body.trim());
    if (audienceType === 'class') {
      fd.append('audience_ids', JSON.stringify(selectedClassIds.map(Number)));
    } else if (audienceType === 'section' && selectedClass) {
      const curriculumClassId = selectedClass.curriculum_class_id ?? selectedClass.id;
      fd.append('audience_id', String(curriculumClassId));
      if (selectedSectionId) fd.append('section_id', String(selectedSectionId));
    } else if (audienceType === 'student' && selectedStudent)
      fd.append('audience_id', String(selectedStudent.id));
    attachmentFiles.forEach(f => fd.append('attachments', f));
    fd.append('send_push', sendPush ? '1' : '0');
    if (notifType === 'ptm' && (meetingDate.trim() || venue.trim())) {
      fd.append('data_payload', JSON.stringify({
        ...(meetingDate.trim() ? { meeting_date: meetingDate.trim() } : {}),
        ...(venue.trim() ? { venue: venue.trim() } : {}),
      }));
    }

    setSending(true);
    try {
      const apiBase = (import.meta.env.VITE_API_URL as string | undefined)?.replace('/api', '') ?? 'http://localhost:5004';
      const token = Cookies.get('erp_auth_token') || Cookies.get('crm_auth_token');
      const resp = await fetch(`${apiBase}/api/parent-app/admin/notifications/broadcast`, {
        method: 'POST', headers: { Authorization: `Bearer ${token ?? ''}` }, credentials: 'include', body: fd,
      });
      const json = await resp.json();
      if (json?.success) {
        const count = json.data?.sent_to ?? 0;
        toastMessage(`Sent to ${count} parent${count !== 1 ? 's' : ''}`, 'success');
        previews.forEach(p => p && URL.revokeObjectURL(p));
        setTitle(''); setBody(''); setAttachmentFiles([]); setPreviews([]);
        setMeetingDate(''); setVenue('');
      } else {
        toastMessage(json?.message ?? 'Failed to send', 'error');
      }
    } catch { toastMessage('Failed to send', 'error'); }
    finally { setSending(false); }
  };

  const currentType = NOTIF_TYPES.find(t => t.value === notifType)!;

  const audienceSummary = () => {
    if (audienceType === 'school') return 'entire school';
    if (audienceType === 'class') {
      if (!selectedClassIds.length) return 'select audience above';
      const names = classGroups.flatMap(g => g.classes)
        .filter(c => selectedClassIds.includes(String(c.id)))
        .map(c => c.class_label);
      return names.length === 1 ? names[0] : `${names.length} classes`;
    }
    if (audienceType === 'section' && selectedClass) {
      const sec = sections.find(s => s.id === selectedSectionId);
      return sec ? `${selectedClass.class_name} · ${sec.section_label}` : selectedClass.class_name;
    }
    if (audienceType === 'student' && selectedStudent) return `parents of ${selectedStudent.display_name}`;
    return 'select audience above';
  };

  return (
    <div style={{ maxWidth: 680 }}>

      {/* Notification type */}
      <div className="field" style={{ marginBottom: 16 }}>
        <label>Notification type</label>
        <select
          className="select"
          value={notifType}
          onChange={e => setNotifType(e.target.value as NotifType)}
          style={{ maxWidth: 340 }}
        >
          {[...new Set(NOTIF_TYPES.map(t => t.group))].map(group => (
            <optgroup key={group} label={group}>
              {NOTIF_TYPES.filter(t => t.group === group).map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* PTM structured fields — optional, only for the PTM/Meeting type */}
      {notifType === 'ptm' && (
        <div className="row gap-10" style={{ marginBottom: 20, maxWidth: 460 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Meeting date (optional)</label>
            <input
              type="date"
              className="input"
              value={meetingDate}
              onChange={e => setMeetingDate(e.target.value)}
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Venue (optional)</label>
            <input
              type="text"
              className="input"
              placeholder="e.g. Classroom LKG-A"
              maxLength={120}
              value={venue}
              onChange={e => setVenue(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Push notification toggle */}
      <div className="row gap-10" style={{ marginBottom: 20, padding: '12px 16px', background: sendPush ? 'var(--surface-tint)' : 'var(--surface-2)', borderRadius: 12, border: `1px solid ${sendPush ? 'var(--accent)' : 'var(--border-2)'}`, maxWidth: 460 }}>
        <input
          type="checkbox"
          id="send_push"
          checked={sendPush}
          onChange={e => setSendPush(e.target.checked)}
          style={{ width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}
        />
        <label htmlFor="send_push" style={{ cursor: 'pointer', flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text)' }}>Send as push notification</div>
          <div className="meta" style={{ marginTop: 2 }}>Parents with the mobile app installed will receive an instant push alert on their phone.</div>
        </label>
      </div>

      {/* Target audience */}
      <div className="field" style={{ marginBottom: 20 }}>
        <label>Target audience</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
          {AUDIENCE_OPTIONS.map(opt => {
            const on = audienceType === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setAudienceType(opt.value)}
                className="card"
                style={{
                  textAlign: 'left', padding: 14, cursor: 'pointer',
                  borderColor: on ? 'var(--slate)' : 'var(--border-2)',
                  borderWidth: on ? 2 : 1,
                  background: on ? 'var(--surface-tint)' : 'var(--surface)',
                }}
              >
                <div className="row gap-10">
                  <div className="center" style={{
                    width: 32, height: 32, borderRadius: 9,
                    background: on ? 'var(--slate)' : 'var(--slate-soft)',
                    color: on ? 'var(--accent)' : 'var(--text-2)',
                  }}>
                    {opt.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 13.5, color: 'var(--text)' }}>{opt.label}</div>
                    <div className="meta" style={{ marginTop: 2 }}>{opt.description}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* By Class — multi-select */}
      {audienceType === 'class' && (
        <div style={{ marginBottom: 20 }}>
          <MultiClassSelect
            classGroups={classGroups}
            selectedIds={selectedClassIds}
            onChange={setSelectedClassIds}
          />
          <ClassFilterChips
            classGroups={classGroups}
            selectedIds={selectedClassIds}
            onChange={setSelectedClassIds}
          />
        </div>
      )}

      {/* By Section — single class then section */}
      {audienceType === 'section' && (
        <div className="row gap-10" style={{ marginBottom: 20 }}>
          <select
            className="select"
            style={{ maxWidth: 240 }}
            value={selectedClass?.id ?? ''}
            onChange={e => {
              setSelectedClass(classes.find(c => c.id === Number(e.target.value)) ?? null);
              setSelectedSectionId(null);
            }}
          >
            <option value="">Select class…</option>
            {[...new Set(classes.map(c => c.curriculum_name))].map(currName => (
              <optgroup key={currName} label={currName}>
                {classes.filter(c => c.curriculum_name === currName).map(c =>
                  <option key={c.id} value={c.id}>{c.class_name}</option>
                )}
              </optgroup>
            ))}
          </select>

          {selectedClass && sections.length > 0 && (
            <select
              className="select"
              style={{ maxWidth: 200 }}
              value={selectedSectionId ?? ''}
              onChange={e => setSelectedSectionId(Number(e.target.value) || null)}
            >
              <option value="">All sections</option>
              {sections.map(s => (
                <option key={s.id} value={s.id}>{s.section_label}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Student search */}
      {audienceType === 'student' && (
        <div className="field" style={{ marginBottom: 20, position: 'relative' }}>
          <label>Search student</label>
          <input
            className="input"
            placeholder="Type student name…"
            value={selectedStudent ? selectedStudent.display_name : studentSearch}
            onChange={e => { setSelectedStudent(null); searchStudents(e.target.value); }}
          />
          {!selectedStudent && students.length > 0 && (
            <div className="card" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, padding: 6, marginTop: 4, boxShadow: 'var(--sh-md)' }}>
              {students.map(s => (
                <button
                  key={s.id}
                  onClick={() => { setSelectedStudent(s); setStudents([]); }}
                  className="nav-item"
                  style={{ borderRadius: 9, padding: '9px 12px', width: '100%' }}
                >
                  <span style={{ fontWeight: 700, fontSize: 13.5 }}>{s.display_name}</span>
                  {s.admission_number && <span className="meta" style={{ marginLeft: 8 }}>#{s.admission_number}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Composer card */}
      <div className="card" style={{ overflow: 'visible' }}>
        {/* Author row */}
        <div className="row gap-12" style={{ padding: '14px 18px', borderBottom: '1px solid var(--divider)' }}>
          <div className="center" style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'var(--slate)', color: 'var(--accent)', flexShrink: 0,
          }}>
            <School size={18} strokeWidth={2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>School Admin</div>
            <div className="row gap-6" style={{ marginTop: 2, flexWrap: 'wrap' }}>
              <span className="meta">Sending to {audienceSummary()}</span>
              <span className={`chip chip-sm ${currentType.cat}`}>{currentType.label}</span>
            </div>
          </div>
        </div>

        {/* Input area */}
        <div style={{ padding: '2px 18px' }}>
          <input
            type="text"
            maxLength={255}
            placeholder="Notification title…"
            value={title}
            onChange={e => setTitle(e.target.value)}
            style={{
              width: '100%', border: 'none', outline: 'none', background: 'transparent',
              fontSize: 18, fontWeight: 800, letterSpacing: '-0.4px',
              padding: '14px 0 8px', color: 'var(--text)', fontFamily: 'inherit',
            }}
          />
          <textarea
            maxLength={1000}
            placeholder="Write your message…"
            value={body}
            onChange={e => setBody(e.target.value)}
            style={{
              width: '100%', border: 'none', outline: 'none', background: 'transparent',
              fontSize: 14.5, lineHeight: 1.6, padding: '0 0 12px',
              color: 'var(--text)', resize: 'none', minHeight: 90, fontFamily: 'inherit',
            }}
          />

          {/* Attachment thumbnails */}
          {attachmentFiles.length > 0 && (
            <div className="row gap-8" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
              {attachmentFiles.map((f, i) => (
                <div key={i} style={{ position: 'relative', width: 76, height: 76 }}>
                  {previews[i] ? (
                    <img src={previews[i]!} alt={f.name} style={{ width: 76, height: 76, borderRadius: 11, objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div className="center" style={{ width: 76, height: 76, borderRadius: 11, background: 'var(--slate-soft)', flexDirection: 'column', gap: 4 }}>
                      <FileText size={22} color="var(--text-3)" />
                      <span style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 700 }}>{f.name.split('.').pop()?.toUpperCase()}</span>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      if (previews[i]) URL.revokeObjectURL(previews[i]!);
                      setAttachmentFiles(p => p.filter((_, j) => j !== i));
                      setPreviews(p => p.filter((_, j) => j !== i));
                    }}
                    className="center"
                    style={{
                      position: 'absolute', top: -6, right: -6,
                      width: 20, height: 20, borderRadius: '50%',
                      background: 'var(--slate)', color: '#fff',
                      border: '2px solid var(--bg)', cursor: 'pointer',
                    }}
                  >
                    <X size={10} strokeWidth={3} />
                  </button>
                </div>
              ))}
              {attachmentFiles.length < MAX_FILES && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: 76, height: 76, borderRadius: 11,
                    border: '2px dashed var(--border-2)', background: 'transparent',
                    color: 'var(--text-3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .14s',
                  }}
                  onMouseEnter={e => { (e.currentTarget.style.borderColor = 'var(--accent-deep)'); (e.currentTarget.style.color = 'var(--accent-deep)'); }}
                  onMouseLeave={e => { (e.currentTarget.style.borderColor = 'var(--border-2)'); (e.currentTarget.style.color = 'var(--text-3)'); }}
                >
                  <span style={{ fontSize: 22, lineHeight: 1 }}>+</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="row" style={{ padding: '11px 18px', borderTop: '1px solid var(--divider)', background: 'var(--surface-2)' }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={attachmentFiles.length >= MAX_FILES}
          >
            <Paperclip size={14} />
            Attach
            {attachmentFiles.length > 0 && (
              <span style={{ color: 'var(--accent-deep)', fontWeight: 800 }}>{attachmentFiles.length}/{MAX_FILES}</span>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
            onChange={e => {
              const picked = Array.from(e.target.files ?? []);
              const newPreviews = picked.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
              setAttachmentFiles(prev => [...prev, ...picked].slice(0, MAX_FILES));
              setPreviews(prev => [...prev, ...newPreviews].slice(0, MAX_FILES));
              e.target.value = '';
            }}
          />
          <div className="spacer" />
          <span className="num meta" style={{ marginRight: 12, color: body.length > 950 ? 'var(--warning)' : 'var(--text-3)' }}>
            {1000 - (title.length + body.length)}
          </span>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
            {sending
              ? <Loader2 size={15} style={{ animation: 'spin .7s linear infinite' }} />
              : <Send size={15} />}
            {sending ? 'Sending…' : 'Send notification'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── History Tab ──────────────────────────────────────────────────────────────

function HistoryTab() {
  const [records, setRecords] = useState<BroadcastRecord[]>([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (p: number) => {
    setLoading(true);
    const res = await notificationsApi.getHistory(p);
    if (res.success && res.data) {
      const payload = res.data as any;
      setRecords(payload.data ?? []);
      setTotal(payload.total ?? 0);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(page); }, [page]);

  return (
    <div style={{ maxWidth: 840 }}>
      {/* Subheader */}
      <div className="row" style={{ marginBottom: 16 }}>
        <span className="meta">{total} broadcast{total !== 1 ? 's' : ''} sent</span>
        <div className="spacer" />
        <button className="btn btn-ghost btn-sm" onClick={() => load(page)}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="card" style={{ padding: '56px 24px', textAlign: 'center' }}>
          <Loader2 size={28} color="var(--text-3)" style={{ animation: 'spin .7s linear infinite', margin: '0 auto' }} />
        </div>
      ) : records.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty-art"><Bell size={32} /></div>
            <h3>No notifications yet</h3>
            <p>Notifications you send will appear here with delivery stats.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="card" style={{ overflow: 'hidden' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Type</th>
                  <th style={{ width: '36%' }}>Title</th>
                  <th>Audience</th>
                  <th style={{ textAlign: 'right' }}>Sent to</th>
                  <th style={{ textAlign: 'right' }}>When</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id}>
                    <td>
                      <span className={`chip chip-sm ${TYPE_CAT[r.type] ?? 'cat-blue'}`}>
                        {TYPE_LABELS[r.type] ?? r.type}
                      </span>
                    </td>
                    <td style={{ fontWeight: 700 }}>{r.title}</td>
                    <td className="muted" style={{ fontSize: 13 }}>{audienceLabel(r)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="row num gap-6" style={{ justifyContent: 'flex-end', fontWeight: 700, fontSize: 13 }}>
                        <CheckCircle size={14} color="var(--success)" />
                        {r.sent_to} <span className="meta" style={{ fontWeight: 600 }}>parents</span>
                      </span>
                    </td>
                    <td className="meta" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 20 && (
            <div className="row gap-8" style={{ marginTop: 16, justifyContent: 'center' }}>
              <button className="btn btn-soft btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                Previous
              </button>
              <span className="meta" style={{ padding: '0 8px' }}>Page {page}</span>
              <button className="btn btn-soft btn-sm" onClick={() => setPage(p => p + 1)} disabled={page * 20 >= total}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function NotificationCenter() {
  const [tab, setTab] = useState<'compose' | 'history'>('compose');

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Page header */}
      <div className="page-head">
        <div className="page-head-icon">
          <Bell size={20} strokeWidth={2} />
        </div>
        <div className="page-head-text">
          <h1>Communication Hub</h1>
          <p>Send targeted push notifications to parents</p>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        {/* Segmented tabs */}
        <div style={{ marginBottom: 24 }}>
          <div className="seg">
            <button className={tab === 'compose' ? 'active' : ''} onClick={() => setTab('compose')}>
              Compose
            </button>
            <button className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>
              History
            </button>
          </div>
        </div>

        {tab === 'compose' ? <ComposeTab /> : <HistoryTab />}
      </div>
    </>
  );
}
